import { InviteLinksGroupChannelsType } from '@/shared/api'
import { useMemo } from 'react'

export interface GroupsFormattedRowType {
    id: number
    groupName: string
    linksCount: number
    budget: number
    creationDate: string
    subscribers: number
    reach: number
    subscriptions: number
    cpm: number
    cps: number
    unsubscriptions: number
    unsubscriptionsPercentage: number
    total_cps: number
}

export const useGroupsFormattedRows = (
    groups: InviteLinksGroupChannelsType[]
): GroupsFormattedRowType[] => {
    const formattedRows = useMemo(() => {
        const rows = groups.map((group) => {
            const links = group.inviteLinks

            const linksCount = links.length
            const budget = links.reduce(
                (sum, link) => sum + (link.budget || 0),
                0
            )
            const creationDate = group.created_at

            const subscribers = links.reduce(
                (sum, link) =>
                    sum + (link.publisher?.telegram_channel_subscribers || 0),
                0
            )

            const reach = links.reduce(
                (sum, link) => sum + (link.publisher?.telegram_post_reach || 0),
                0
            )

            const subscriptions = links.reduce(
                (sum, link) => sum + (link.subscribed_count || 0),
                0
            )

            const totalCost = budget
            const totalImpressions = reach
            const cpm =
                totalImpressions > 0 ? (totalCost / totalImpressions) * 1000 : 0
            const cps = subscriptions > 0 ? totalCost / subscriptions : 0

            const unsubscriptions = links.reduce(
                (sum, link) => sum + (link.unsubscribed_count || 0),
                0
            )
            const unsubscriptionsPercentage =
                subscriptions > 0 ? (unsubscriptions / subscriptions) * 100 : 0

            return {
                id: group.invite_links_group_id,
                groupName: group.group_name,
                linksCount,
                budget,
                creationDate,
                subscribers,
                reach,
                subscriptions,
                cpm,
                cps,
                unsubscriptions,
                unsubscriptionsPercentage,
                total_cps: 0,
            }
        })

        const allLinksGroupDb = rows.find(
            (row) => row.groupName === 'Все ссылки'
        )
        const filteredRows = rows.filter(
            (row) => row.groupName !== 'Все ссылки'
        )

        const aggregate = filteredRows.reduce(
            (acc, row) => {
                acc.linksCount += row.linksCount
                acc.budget += row.budget
                acc.subscribers += row.subscribers
                acc.reach += row.reach
                acc.subscriptions += row.subscriptions
                acc.unsubscriptions += row.unsubscriptions
                return acc
            },
            {
                linksCount: 0,
                budget: 0,
                subscribers: 0,
                reach: 0,
                subscriptions: 0,
                unsubscriptions: 0,
            }
        )

        const cpm =
            aggregate.reach > 0
                ? (aggregate.budget / aggregate.reach) * 1000
                : 0
        const cps =
            aggregate.subscriptions > 0
                ? aggregate.budget / aggregate.subscriptions
                : 0
        const unsubscriptionsPercentage =
            aggregate.subscriptions > 0
                ? (aggregate.unsubscriptions / aggregate.subscriptions) * 100
                : 0

        const allLinksGroup: GroupsFormattedRowType = {
            id: allLinksGroupDb?.id || 0,
            groupName: 'Все ссылки',
            linksCount: aggregate.linksCount,
            budget: aggregate.budget,
            creationDate: allLinksGroupDb?.creationDate || '',
            subscribers: aggregate.subscribers,
            reach: aggregate.reach,
            subscriptions: aggregate.subscriptions,
            cpm,
            cps,
            unsubscriptions: aggregate.unsubscriptions,
            unsubscriptionsPercentage,
            total_cps: 0,
        }

        return [...(rows.length ? [allLinksGroup] : []), ...filteredRows]
    }, [groups])

    return formattedRows
}
